import { useEffect } from "react";
import { Reveal, SectionHead } from "@/components/Reveal";
import WaitlistForm from "@/components/WaitlistForm";

const SYSTEMS = [
  {
    name: "FOUNDER OS",
    tagline: "Build the business behind the idea.",
    testid: "os-page-founder",
  },
  {
    name: "BUSINESS OS",
    tagline: "Build a more scalable way of working.",
    testid: "os-page-business",
  },
  {
    name: "NONPROFIT OS",
    tagline: "Strengthen programs, processes and organizational capacity.",
    testid: "os-page-nonprofit",
  },
];

const METHOD = ["ASSESS", "CLARIFY", "PRIORITIZE", "BUILD", "LEARN", "ADAPT"];

export default function OS() {
  useEffect(() => {
    document.title = "The KP Strategy OS — KP Strategy";
    return () => {
      document.title =
        "KP Strategy — Design your business like a product. Run it like a system. Scale it with AI.";
    };
  }, []);

  return (
    <main data-testid="os-page">
      {/* Hero */}
      <section className="pt-[72px]" data-testid="os-hero">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-16 sm:px-6 sm:pt-24 lg:px-8 lg:pb-20 lg:pt-28">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-xs font-medium uppercase tracking-[0.3em] text-amber">
              <span className="inline-block h-px w-10 bg-amber" aria-hidden="true" />
              Coming Soon
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-7 max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl md:text-6xl">
              The KP Strategy OS
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-body sm:text-lg">
              Practical operating systems for people building businesses,
              products and organizations.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Three systems */}
      <section className="border-t border-line py-20 sm:py-24" data-testid="os-systems">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            chapter="01 — The Ecosystem"
            title="One philosophy. Three operating systems."
            copy="The same product-and-systems thinking KP Strategy brings to every engagement — being shaped into practical tools you can use yourself."
            testid="os-systems-head"
          />
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
            {SYSTEMS.map((s, i) => (
              <Reveal key={s.name} delay={i * 0.1} className="h-full">
                <div
                  data-testid={s.testid}
                  className="flex h-full flex-col border border-ink/15 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-amber/60 sm:p-10"
                >
                  <div className="flex items-center justify-between gap-4">
                    <h3 className="font-mono text-sm font-semibold uppercase tracking-[0.2em] text-ink">
                      {s.name}
                    </h3>
                    <span className="whitespace-nowrap rounded-full border border-amber/50 bg-amber-soft px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-amber-dark">
                      Coming Soon
                    </span>
                  </div>
                  <span className="mt-6 block h-px w-full bg-line" />
                  <p className="mt-6 font-serif text-xl font-light leading-snug text-body">
                    {s.tagline}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <ol className="mt-12 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-0" data-testid="os-methodology-strip">
              {METHOD.map((step, i) => (
                <li key={step} className="flex items-center">
                  <span className="flex items-baseline gap-2">
                    <span className="font-mono text-[11px] text-amber">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-mono text-sm font-medium uppercase tracking-[0.15em] text-ink">
                      {step}
                    </span>
                  </span>
                  {i < METHOD.length - 1 && (
                    <span className="hidden px-4 text-ink/30 sm:inline" aria-hidden="true">
                      →
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
      </section>

      {/* Waitlist */}
      <section className="border-t border-line bg-parch py-20 sm:py-28" data-testid="os-waitlist">
        <div className="mx-auto max-w-2xl px-4 text-center sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber">
              02 — The Waitlist
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-3xl font-light leading-[1.12] tracking-tight text-ink sm:text-4xl">
              Want to be the first to know?
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-body sm:text-lg">
              The KP Strategy OSs are coming soon. Leave your email and
              we&apos;ll keep you posted as each one launches.
            </p>
          </Reveal>
          <Reveal delay={0.26}>
            <WaitlistForm testid="os-waitlist" />
          </Reveal>
        </div>
      </section>
    </main>
  );
}
